import { Dispatch, SetStateAction, MouseEvent, ReactNode } from 'react'

// Third party
import { ChevronDown, Info } from 'lucide-react'

import { Tooltip, TooltipContent, TooltipTrigger } from '../../../../../../components/ui/tooltip'
import { Checkbox } from '../../../../../../components/ui/checkbox'

import { cn } from '../../../../../../lib/utils'

import { IRegistrationEventTab } from '../../../../../../types/competitor_types'

import { CONST } from '../../../../../../const/const'

// Constants
const ADD_ICON_CLASS = 'add_icon'
const REMOVE_ICON_CLASS = 'remove_icon'
const MAIN_DIV_CLASS = 'parent_div'

// Types
type ConfirmationCompsWrapperProps = {
  title:
    | 'Tickets'
    | 'Registrations'
    | 'Fees'
    | 'Mandatory Fees'
    | 'Grand total'
    | 'Paperwork'
    | 'Summary'
    | 'Order Summary'
  tooltipText?: string
  selected_units?: string
  showList: boolean
  accordion?: { open: boolean; setOpen: Dispatch<SetStateAction<boolean>> }
  hideToggleIcon?: boolean
  grandTotal?: string
  activeTab?: {
    tab: string
    step: number
  }
  disableEditBtn?: boolean
  setEventTab?: (eventTab: IRegistrationEventTab) => void
  redirectTab?: string
  isSplitBill?: boolean
  children: ReactNode
  isSendBillToAllChecked?: boolean
  onSendBillToAll?: (state: boolean) => Promise<void>
}

const ConfirmationCompsWrapper = ({
  title,
  accordion,
  setEventTab,
  redirectTab,
  tooltipText,
  disableEditBtn,
  selected_units,
  grandTotal,
  showList,
  hideToggleIcon,
  isSplitBill,
  children,
  onSendBillToAll,
  isSendBillToAllChecked,
}: ConfirmationCompsWrapperProps) => {
  const openNextSibling = (e: MouseEvent<HTMLDivElement>) => {
    let target = e.target as HTMLElement | null

    // If the target is a path or SVG, find the SVG to handle the click properly.
    if (target?.tagName.toLowerCase() === 'path' || target?.tagName.toLowerCase() === 'svg') {
      target = (target.closest('svg') as HTMLElement | null) ?? target
    }

    // Check if any of the target's parent elements has one of the specified classes.
    if (
      target &&
      (target.classList.contains(MAIN_DIV_CLASS) ||
        target.closest(`.${ADD_ICON_CLASS}`) ||
        target.closest(`.${REMOVE_ICON_CLASS}`))
    ) {
      accordion?.setOpen(!accordion?.open)
    }
  }

  const navigate = () => {
    if (!setEventTab) return
    switch (redirectTab) {
      case CONST.UI.REGISTER.TABS.REGISTER:
        return setEventTab({
          tab: CONST.UI.REGISTER.TABS.REGISTER,
          step: 0,
        })
      case CONST.UI.REGISTER.TABS.FEES:
        return setEventTab({
          tab: CONST.UI.REGISTER.TABS.FEES,
          step: 0,
        })

      case CONST.UI.REGISTER.TABS.PAPERWORK:
        return setEventTab({
          tab: CONST.UI.REGISTER.TABS.PAPERWORK,
          step: 0,
        })

      case CONST.UI.REGISTER.TABS.TICKETS:
        return setEventTab({
          tab: CONST.UI.REGISTER.TABS.FEES,
          step: 1,
        })
    }
  }

  return (
    <div className="mt-4 border-[#E6EAF2] border rounded-lg" onClick={openNextSibling}>
      <header
        className={cn(
          'p-4 text-SeabiscuitDark200ThemeColor flex gap-3 flex-wrap w-full justify-between items-center cursor-pointer',
          MAIN_DIV_CLASS,
          accordion?.open && 'border-b border-gray-200'
        )}
      >
        <div className="flex items-center">
          <h3
            className={cn('text-base md:text-lg', !accordion?.open ? 'font-normal' : 'font-bold')}
          >
            {title}
          </h3>
          {tooltipText ? (
            <Tooltip>
              <TooltipTrigger>
                <Info className="ml-2 w-4 text-gray-400 cursor-help" />
              </TooltipTrigger>
              <TooltipContent>{tooltipText}</TooltipContent>
            </Tooltip>
          ) : null}
        </div>

        <div className="flex flex-wrap gap-3 items-center text-xs md:text-sm justify-end ml-auto">
          {isSplitBill && showList && accordion?.open && (
            <label className="flex items-center space-x-2">
              <span className="text-sm text-gray-600">Send bill to all</span>
              <Checkbox checked={isSendBillToAllChecked} onCheckedChange={onSendBillToAll} />
            </label>
          )}
          {!disableEditBtn ? (
            <>
              <span
                className="font-normal cursor-pointer text-SeabiscuitLightParagraphTextColor transition-all underline hover:no-underline"
                onClick={navigate}
              >
                Edit
              </span>
              {selected_units && (
                <span className="font-bold cursor-auto text-SeabiscuitGreenThemeColor bg-SeabiscuitGreenThemeColor/10 rounded-full py-1 px-2.5">
                  {selected_units}
                </span>
              )}
            </>
          ) : (
            <span className="text-xs md:text-sm font-bold cursor-auto text-SeabiscuitGreenThemeColor bg-SeabiscuitGreenThemeColor/10 rounded-full py-1 px-2.5">
              {grandTotal}
            </span>
          )}
          {!hideToggleIcon && (
            <div
              className={cn(
                'status rounded-full w-7 h-7 flex items-center justify-center transition-all',
                !accordion?.open
                  ? 'text-SeabiscuitDark200ThemeColor bg-input'
                  : 'text-SeabiscuitMainThemeColor bg-SeabiscuitMainThemeColor/5'
              )}
            >
              {accordion?.open ? (
                <div className={REMOVE_ICON_CLASS}>
                  <ChevronDown className="w-4 rotate-180 transition-all" />
                </div>
              ) : (
                <div className={ADD_ICON_CLASS}>
                  <ChevronDown className="w-4 transition-all" />
                </div>
              )}
            </div>
          )}
        </div>
      </header>

      <section className={cn('faqs-content', accordion?.open && 'activeTab')}>
        {showList ? (
          children
        ) : (
          <div className="p-4 text-sm text-SeabiscuitDark200ThemeColor flex items-center justify-center">
            No {title.toLowerCase()} added to this event
          </div>
        )}
      </section>
    </div>
  )
}

export default ConfirmationCompsWrapper
